import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingNewExecution,
  setCrossClientLandingExecution,
  setCrossClientEvaluateLoading,
  setCrossClientInitLoading,
} from '../../../store/crossClient/crossClientLanding'
import { useEvaluateCrossClientQueryMutation, useInitializeCrossClientQueryMutation } from '../../../store/crossClient/crossClientExecution'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { CrossClientQueryDataSource, CrossClientQueryLogicalQuestionDecomposition, CrossClientQueryPhases } from '../../../models/CrossClient'

export const useCrossClientQueryLandingExecution = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const newExecution = useAppSelector(selectCrossClientLandingNewExecution)

  const [triggerInitializeCrossClientQuery] = useInitializeCrossClientQueryMutation()
  const [triggerEvaluateCrossClientQuery] = useEvaluateCrossClientQueryMutation()

  const initializeCrossClientExecution = useCallback(
    async (userQuestion: string, decomposition?: CrossClientQueryLogicalQuestionDecomposition) => {
      try {
        dispatch(setCrossClientLandingExecution(null))
        dispatch(setCrossClientInitLoading(true))
        const execution = await triggerInitializeCrossClientQuery({
          accountId,
          payload: { ownerId: currentUserId, userQuestion, decomposition },
        }).unwrap()
        dispatch(setCrossClientLandingExecution(execution))
        return execution
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientInitLoading(false))
      }
    },
    [dispatch, accountId, currentUserId, triggerInitializeCrossClientQuery],
  )

  const evaluateCrossClientExecution = useCallback(
    async (executionId: string, sources: CrossClientQueryDataSource[]) => {
      try {
        dispatch(setCrossClientEvaluateLoading(true))
        await triggerEvaluateCrossClientQuery({ accountId, executionId, payload: { sources } }).unwrap()
        navigate(`/cross-client/executions/${executionId}`, { replace: true })
      } catch (e) {
        console.error(e)
        dispatch(setCrossClientEvaluateLoading(false))
      }
    },
    [dispatch, navigate, accountId, triggerEvaluateCrossClientQuery],
  )

  const initError = useMemo(() => {
    if (!newExecution) {
      return null
    }
    const { phase } = newExecution
    if (
      phase === CrossClientQueryPhases.NO_CLIENTS_WERE_FOUND ||
      phase === CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_IDS ||
      phase === CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_ROLE
    ) {
      return 'No clients found'
    }
    if (newExecution.phase === CrossClientQueryPhases.QUESTION_ERROR) {
      return 'Unable to decompose question'
    }
    return null
  }, [newExecution])

  const resetExecution = useCallback(() => {
    dispatch(setCrossClientLandingExecution(null))
  }, [dispatch])

  return {
    initError,
    initializeCrossClientExecution,
    evaluateCrossClientExecution,
    resetExecution,
  }
}
